import { EditDataType, EditMetaData, OccurrenceDetailsType } from '@/shared/types'
import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react'
import { BACKEND_URL } from '../util/config'
import type { RootState } from './store'

type RefreshTokenResult = {
  data?: { token?: string }
  error?: FetchBaseQueryError
  meta?: FetchBaseQueryMeta
}

const REQUEST_TIMEOUT_MS = 60_000

const baseQuery = fetchBaseQuery({
  baseUrl: BACKEND_URL,
  timeout: REQUEST_TIMEOUT_MS,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).user.token
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    return headers
  },
})

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  let result = await baseQuery(args, api, extraOptions)

  if (result.error?.status === 503) {
    let isMaintenance = false
    const data = result.error.data
    if (data && typeof data === 'object') {
      const candidate = data as { maintenance?: unknown; enabledAt?: unknown; reason?: unknown }
      if (candidate.maintenance === true) {
        isMaintenance = true
        const enabledAt = typeof candidate.enabledAt === 'string' ? candidate.enabledAt : null
        const reason = typeof candidate.reason === 'string' ? candidate.reason : null
        sessionStorage.setItem('nowdb_maintenance', JSON.stringify({ enabledAt, reason }))
      }
    }

    if (isMaintenance && window.location.pathname !== '/maintenance') {
      window.location.replace('/maintenance')
    }
    return result
  }

  const token = (api.getState() as RootState).user.token
  if (token && result.error?.status === 401) {
    const refreshedToken = (await baseQuery(
      { url: '/refreshToken', method: 'POST', body: { token } },
      api,
      extraOptions
    )) as RefreshTokenResult
    if (refreshedToken?.data?.token) {
      api.dispatch({ type: 'user/setToken', payload: refreshedToken.data.token })
      result = await baseQuery(args, api, extraOptions)
    } else {
      api.dispatch({ type: 'user/clearUser' })
      localStorage.clear()
      window.location.replace('/login?expired=true')
    }
  }
  return result
}

export const api = createApi({
  reducerPath: 'api',
  tagTypes: [
    'museum',
    'user',
    'crosssearch',
    'locality',
    'localities',
    'species',
    'specieslist',
    'speciessynonyms',
    'reference',
    'references',
    'timeunits',
    'timeunit',
    'timebound',
    'timebounds',
    'region',
    'regions',
    'museum',
    'museums',
    'person',
    'persons',
    'author',
    'project',
    'projects',
    'geoname',
    'occurrence',
  ],
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
})

const occurrenceApi = api.injectEndpoints({
  endpoints: builder => ({
    getOccurrenceDetails: builder.query<OccurrenceDetailsType, { lid: number; speciesId: number }>({
      query: ({ lid, speciesId }) => ({
        url: `/occurrence/${lid}/${speciesId}`,
      }),
      providesTags: result => (result ? [{ type: 'occurrence', id: `${result.lid}-${result.species_id}` }] : []),
    }),
    editOccurrence: builder.mutation<OccurrenceDetailsType, EditDataType<OccurrenceDetailsType> & EditMetaData>({
      query: occurrence => ({
        url: `/occurrence/${occurrence.lid}/${occurrence.species_id}`,
        method: 'PUT',
        body: { occurrence },
      }),
      invalidatesTags: (_result, _error, { lid, species_id }) => [{ type: 'occurrence', id: `${lid}-${species_id}` }],
    }),
  }),
})

export const { useGetOccurrenceDetailsQuery, useEditOccurrenceMutation } = occurrenceApi
